import React, { useState, useEffect, useRef } from "react";
import { Input } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { FaCaretUp, FaCaretDown } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import {
  getSearchProjects,
  RESETSEARCH,
} from "../../redux/features/projects/projectsSlice";
import { Link } from "react-router-dom";
import { Skeleton } from "@mantine/core";
import { TbFileArrowRight } from "react-icons/tb";
import FormattedNumber from "../../utils/fixPrice";
import calculatePercentageChange from "../../utils/percentageMove";
import BigValueFormatter from "../../utils/bigNumbers";
import logoLight from "../../assets/images/logo-orange-light.svg";
import logoDark from "../../assets/images/logo-orange.svg";
import LogoFreshCoinsLight from "../../assets/images/freshcoins_logo_var-03.svg";
import LogoFreshCoinsDark from "../../assets/images/freshcoins_logo_var-04.svg";

const InputProjects = ({
  searchTrendingRef,
  searchOpen,
  handleSearchOpen,
  projects,
  computedColorScheme,
}) => {
  const dispatch = useDispatch();
  const { search, isLoadingSearch } = useSelector((state) => state.projects);
  const [hideTrending, setHideTrending] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [activeIndex, setActiveIndex] = useState(null);

  // Ref for the input element
  const inputRef = useRef(null);

  useEffect(() => {
    // Function to handle clicks outside the input
    const handleClickOutside = (event) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        inputValue.trim() !== ""
      ) {
        setInputValue("");
        setHideTrending(false);
        dispatch(RESETSEARCH());
      }
    };

    // Attach the event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [inputValue]);

  const handleSearch = (event) => {
    const value = event.target.value;
    setInputValue(value);

    // Update hideTrending based on the input value
    setHideTrending(value.trim() !== ""); // hide if input is not empty

    if (value !== "") {
      let inputValue = {
        searchTerm: event.target.value,
      };

      dispatch(getSearchProjects(inputValue));
    }
  };
  return (
    <div
      className="flex-grow"
      style={{ maxWidth: "620px" }}
      ref={searchTrendingRef}
    >
      <div className="hidden items-center gap-2 sm:flex">
        <div className="flex items-center gap-2">
          <Link className="relative shrink-0" to="/">
            <img
              src={
                computedColorScheme === "dark"
                  ? LogoFreshCoinsLight
                  : LogoFreshCoinsDark
              }
              width="180px"
              style={{ marginLeft: "-12px", position: "relative" }}
            />
            <span className="logo-text">AUDITS</span>
          </Link>
        </div>
        <div className="flex-grow" style={{ maxWidth: "620px" }}>
          <Input
            onClick={handleSearchOpen}
            className="search-token"
            placeholder="Search by name or address"
            leftSection={<IconSearch size={20} />}
            value={inputValue}
            onChange={(e) => handleSearch(e)}
            ref={inputRef}
          />
        </div>
      </div>
      <div
        className={`${
          searchOpen ? "transition transform ease-out opacity-100" : "hidden"
        }`}
      >
        <ul
          aria-labelledby="headlessui-combobox-button-:r1hq:"
          role="listbox"
          id="headlessui-combobox-options-:r1nc:"
          data-headlessui-state="open"
        >
          <div
            className={`absolute rounded-xl border border-n-20 bg-n-0 mt-2 w-full px-0 pb-0 pt-2 sm:min-w-[480px] shadow-lg shadow-black-5 dark:shadow-black-40 max-h-[480px] overflow-auto scrollbar-hide`}
          >
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-2 empty:hidden">
                <div className="text-sm font-medium text-n-70 sm:mx-4">
                  {hideTrending && search?.length > 0 ? "Projects" : ""}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-2 empty:hidden">
                <div className="text-sm font-medium text-n-70 sm:mx-4">
                  {hideTrending ? "" : "Trending Projects"}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-2 empty:hidden">
                <div className="pb-2 text-xs font-normal text-n-50 sm:px-4">
                  {search?.length > 0 || inputValue === ""
                    ? ""
                    : "No results were found for your search"}
                </div>
              </div>
            </div>
            <div role="none">
              <div
                className="grid"
                role="none"
                style={{
                  position: "static",
                  gridTemplateColumns:
                    "max-content minmax(100px, 2fr) minmax(max-content, 1.5fr) minmax(max-content, 1.5fr)",
                }}
              >
                {(hideTrending ? search : projects)
                  ?.slice(0, 5)
                  .map((p, index) => (
                    <Link
                      to="/"
                      className={`group contents text-n-100 [&>*]:border-b [&>*]:border-n-10 [&>*]:last:border-0 [&>*]:transition-colors `}
                      key={index}
                      onMouseEnter={() => setActiveIndex(index)}
                      onMouseLeave={() => setActiveIndex(null)}
                      data-headlessui-state={
                        activeIndex === index ? "active" : ""
                      }
                    >
                      <div className="flex h-[60px] shrink-0 items-center py-2 pl-0 pr-1.5 sm:pl-4 group-hover:bg-n-5 ui-active:bg-n-5">
                        <div
                          className="relative flex w-fit shrink-0 items-center justify-center overflow-hidden rounded-full"
                          style={{ height: "32px", width: "32px" }}
                        >
                          <img
                            alt={`${p.name} logo`}
                            loading="lazy"
                            width="32"
                            height="32"
                            decoding="async"
                            data-nimg="1"
                            className=""
                            srcset={p.logo}
                            style={{ color: "transparent" }}
                          />
                        </div>
                      </div>
                      <div className="flex min-w-0 flex-col justify-content-center gap-2 px-1.5 py-2 group-hover:bg-n-5 ui-active:bg-n-5">
                        <div className="flex min-w-0 items-center gap-1 text-xs">
                          <h1 className="truncate font-medium text-n-100 lg:max-w-[100px]">
                            {p.name}
                          </h1>
                          <p className="truncate font-normal uppercase text-n-50 lg:max-w-[100px]"></p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1 truncate rounded px-1 py-0.5 font-semibold capitalize bg-neutral-10 text-neutral-100 dark:bg-neutral-90 dark:text-neutral-0 text-2xs">
                            <div className="text-n-100">{p.symbol}</div>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 items-start justify-content-center group-hover:bg-n-5 ui-active:bg-n-5">
                        {p.priceUsd ? (
                          <>
                            <div className="flex flex-col items-start gap-1 text-xs text-n-100 sm:flex-row sm:items-center">
                              <div className="cursor-pointer">
                                <FormattedNumber
                                  number={p?.priceUsd?.toFixed(24)}
                                />
                              </div>
                              <div className="flex w-fit items-center rounded text-success-main bg-transparent font-medium text-xs">
                                {p?.priceUsd24hAgo?.toFixed(24) ===
                                undefined ? (
                                  <span></span>
                                ) : (
                                  <div
                                    className={`flex w-fit items-center rounded ${
                                      p?.priceUsd?.toFixed(24) >
                                      p?.priceUsd24hAgo?.toFixed(24)
                                        ? "text-success-main"
                                        : "text-primary-main"
                                    } bg-transparent font-medium text-sm`}
                                  >
                                    {p?.priceUsd?.toFixed(24) >
                                    p?.priceUsd24hAgo?.toFixed(24) ? (
                                      <FaCaretUp />
                                    ) : (
                                      <FaCaretDown />
                                    )}
                                    {calculatePercentageChange(
                                      p?.priceUsd?.toFixed(24),
                                      p?.priceUsd24hAgo?.toFixed(24)
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 font-medium">
                              <div className="text-2xs text-n-80">
                                Market Cap
                              </div>
                              <div className="text-xs text-n-100">
                                <BigValueFormatter value={p?.marketCapUsd} />
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="flex flex-col items-start gap-1 text-xs text-n-100 sm:flex-row sm:items-center">
                            <span className="text-n-40">N/A</span>
                          </div>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center justify-end gap-3 pr-0 sm:pr-4 group-hover:bg-n-5 ui-active:bg-n-5">
                        <div className="flex w-18 items-center justify-center gap-2 sm:w-19 sm:gap-3">
                          <div className="flex flex-col items-end font-semibold text-score-good">
                            <div>
                              <span className="text-base">{p.score}</span>
                              <span className="text-xs">.00</span>
                            </div>
                          </div>
                        </div>
                        <div className="hidden lg:flex">
                          <div className="opacity-0 transition-opacity ui-active:opacity-100">
                            <span className="flex items-center justify-center min-w-[1.25rem] rounded-md px-1.5 py-0.5 text-xs font-semibold opacity-50 transition-all bg-neutral-20 text-neutral-80 dark:bg-neutral-80 dark:text-neutral-20">
                              <TbFileArrowRight className="text-lg" />
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </ul>
      </div>
    </div>
  );
};

export default InputProjects;
